import React, { useContext, useMemo, useState } from "react";
import { CheckCircle, ChevronRight, Edit, Settings, Sync } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";

import { NavigationButton, Progress } from "ui";

import { useGetLayerFeatureTotalsQuery } from "fond/api";
import { LayoutContext } from "fond/layout/LayoutProvider";
import { SubLayoutPopout } from "fond/layout/SubLayoutPopout";
import { openArchitectureModal } from "fond/project/redux";
import { useAppDispatch } from "fond/utils/hooks";
import { ConfirmModal, useStackedNavigationContext } from "fond/widgets";

import DrawModeBar from "./flyout/DrawModeBar";
import { useBaseDataDraw } from "./flyout/useBaseDataDraw";
import BaseDataFlyout from "./BaseDataFlyout";
import BaseDataRow from "./BaseDataRow";
import { formatList } from "./gating";
import { BASE_DATA_GROUPS, BASE_DATA_LAYER_KEYS, BASE_DATA_LAYERS, BaseDataLayerKey } from "./layers";
import ModifyDataFlyout from "./ModifyDataFlyout";
import { BaseDataRowState, getBaseDataRowState } from "./rowState";
import { BaseDataGeneration } from "./useBaseDataGeneration";

const FLYOUT_RECT = { x: 4, y: 38, width: 380 };
const FLYOUT_BOTTOM_MARGIN = 8;

/** What the map flyout shows: a base data layer, or the Modify data tools. */
type FlyoutTarget = BaseDataLayerKey | "modifyData";

interface BaseDataPanelProps {
  versionId: string;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
  generation: BaseDataGeneration;
}

/** Return the height that makes the flyout fill the map sub-layout, or undefined to fit its content. */
const useFlyoutHeight = (open: boolean): number | undefined => {
  const layout = useContext(LayoutContext);
  return useMemo(() => {
    if (!open) return undefined;
    const mapHeight = layout?.mapModel?.getRootRow().getRect().height ?? 0;
    const height = mapHeight - FLYOUT_RECT.y - FLYOUT_BOTTOM_MARGIN;
    return height > 0 ? height : undefined;
  }, [open, layout?.mapModel]);
};

/** The Input Data step of a design project: base data layer rows, their generation and clean-up tools. */
const BaseDataPanel: React.FC<BaseDataPanelProps> = ({ versionId, readOnly, generation }: BaseDataPanelProps) => {
  const dispatch = useAppDispatch();
  const { stack } = useStackedNavigationContext();
  const { currentData: featureTotals } = useGetLayerFeatureTotalsQuery({ versionId }, { skip: !versionId });
  const [flyoutTarget, setFlyoutTarget] = useState<FlyoutTarget | null>(null);
  const { drawingKey, startDraw, cancel: cancelDraw, finish: finishDraw } = useBaseDataDraw();
  const { present, hasData, missing, staleness, gating, running, queued, isRunning, isStarting, failed, progressSteps, confirmation } = generation;

  const counts = useMemo(
    () =>
      Object.fromEntries(
        BASE_DATA_LAYER_KEYS.map((key) => {
          const [layerId] = BASE_DATA_LAYERS[key].layerIds;
          return [key, layerId ? featureTotals?.[layerId]?.count : undefined];
        })
      ) as Record<BaseDataLayerKey, number | null | undefined>,
    [featureTotals]
  );

  const rowStates = useMemo(
    () =>
      Object.fromEntries(
        BASE_DATA_LAYER_KEYS.map((key) => [
          key,
          getBaseDataRowState({
            key,
            present,
            missing,
            staleness,
            generate: gating.generate,
            generatedFrom: gating.generatedFrom,
            nextKey: gating.nextKey,
            loading: false,
            running,
            queued,
            count: counts[key],
          }),
        ])
      ) as Record<BaseDataLayerKey, BaseDataRowState>,
    [present, missing, staleness, gating.generate, gating.generatedFrom, gating.nextKey, running, queued, counts]
  );

  // Hide the flyout while a tool is open in the left panel.
  const isFlyoutOpen = flyoutTarget !== null && !drawingKey && !isRunning && stack.length === 0;
  const flyoutHeight = useFlyoutHeight(isFlyoutOpen);
  const isStale = staleness.ugStale || staleness.leadinsStale;
  const { panelButton } = gating;

  const handleOpenSettings = () => {
    dispatch(openArchitectureModal("DesignPreferences"));
  };

  const toggleFlyout = (target: FlyoutTarget) => {
    setFlyoutTarget((current) => (current === target ? null : target));
  };

  const overwrittenNames = (confirmation ?? []).filter((key) => hasData[key]).map((key) => BASE_DATA_LAYERS[key].name.toLowerCase());

  return (
    <>
      {BASE_DATA_GROUPS.map((group) => (
        <Box key={group.id} mb={1.5} role="group" aria-label={group.label}>
          <Typography variant="overline" color="text.secondary" fontWeight={500} display="block" lineHeight={2} px={0.25}>
            {group.label}
          </Typography>
          <Box display="flex" flexDirection="column">
            {group.keys.map((key) => (
              <BaseDataRow
                key={key}
                layerKey={key}
                rowState={rowStates[key]}
                count={counts[key]}
                selected={flyoutTarget === key}
                disabled={readOnly || isRunning}
                onClick={() => toggleFlyout(key)}
              />
            ))}
          </Box>
        </Box>
      ))}

      {isStale && !isRunning && (
        <Alert severity="warning" variant="compact" icon={<Sync />} sx={{ mb: 1 }}>
          Your data has changed since generation. Generate again to update the out-of-date layers.
        </Alert>
      )}

      {failed.length > 0 && !isRunning && (
        <>
          <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
            <AlertTitle>Base data generation failed</AlertTitle>
            Data could not be generated.
          </Alert>
          <Progress steps={progressSteps} />
        </>
      )}

      {isRunning ? (
        <>
          <Progress steps={progressSteps} />
          <Button fullWidth variant="outlined" size="small" sx={{ my: 1 }} disabled={readOnly || running.length === 0} onClick={generation.cancel}>
            Cancel
          </Button>
        </>
      ) : (
        <Box display="flex" alignItems="center" gap={1} my={1}>
          {panelButton.visible ? (
            <Tooltip title={panelButton.reason ?? ""}>
              <Box flexGrow={1}>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  disabled={!panelButton.enabled || isStarting}
                  onClick={() => generation.run(gating.pending)}
                >
                  {panelButton.label}
                </Button>
              </Box>
            </Tooltip>
          ) : (
            <Box flexGrow={1} display="flex" alignItems="center" gap={1}>
              {gating.isComplete && (
                <>
                  <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 20 }} />
                  <Typography variant="body2">Base data ready for design</Typography>
                </>
              )}
            </Box>
          )}
          <Tooltip title="Generation settings">
            <span>
              <IconButton aria-label="Generation settings" size="small" color="primary" disabled={readOnly} onClick={handleOpenSettings}>
                <Settings />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {!isRunning && (
        <NavigationButton
          icon={<Edit />}
          iconSize="small"
          title="Modify data"
          subtitle="Edit layers, preference path, and review parcels"
          variant="outlined"
          disabled={readOnly}
          onClick={() => toggleFlyout("modifyData")}
          rightElement={<ChevronRight sx={{ fontSize: 20, color: flyoutTarget === "modifyData" ? "primary.main" : "action.disabled" }} />}
          sx={{
            mt: 2,
            mb: 0,
            ...(flyoutTarget === "modifyData" && { backgroundColor: "rgba(33, 150, 243, 0.05)", borderColor: "primary.main" }),
          }}
        />
      )}

      <SubLayoutPopout tabId="base-data-flyout" open={isFlyoutOpen} rect={{ ...FLYOUT_RECT, height: flyoutHeight }} belowModals>
        {flyoutTarget === "modifyData" && <ModifyDataFlyout gates={gating.modify} onClose={() => setFlyoutTarget(null)} />}
        {flyoutTarget && flyoutTarget !== "modifyData" && (
          <BaseDataFlyout
            key={flyoutTarget}
            layerKey={flyoutTarget}
            rowState={rowStates[flyoutTarget]}
            generation={generation}
            readOnly={readOnly}
            onSelect={setFlyoutTarget}
            onStartDraw={startDraw}
          />
        )}
      </SubLayoutPopout>

      {drawingKey && <DrawModeBar drawingKey={drawingKey} onCancel={cancelDraw} onDone={finishDraw} />}

      {confirmation && (
        <ConfirmModal
          open
          header="Generate again?"
          confirmText="Generate"
          content={
            <Typography variant="body2">
              {`Generating replaces the existing ${formatList(overwrittenNames)}. Any edits you've made to ${
                overwrittenNames.length === 1 ? "it" : "them"
              } will be lost.`}
            </Typography>
          }
          onConfirm={generation.confirmRun}
          onCancel={generation.dismissConfirmation}
        />
      )}
    </>
  );
};

export default BaseDataPanel;
